import React, { useState, useEffect, useRef } from "react";
import axios from 'axios';
import { FaArrowLeft, FaCogs, FaSave, FaPlus, FaGripLines, FaAlignLeft, FaAlignCenter, FaAlignRight, FaTrash, FaEdit, FaUserCheck, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { checkIsAdmin, checkIsGlobalAdmin } from "../../utils/authUtils";
import CustomerCustomizationModal from "./CustomerCustomizationModal";

const PrintSettingsPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    branch_name: "",
    headerBlocks: [], // Array of { id, text, align, label }
    footerBlocks: [], // Array of { id, text, align, label }
  });
  const [settingsList, setSettingsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [warningType, setWarningType] = useState('success');
  const [deleteId, setDeleteId] = useState(null);
  const [showList, setShowList] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  
  // Customization
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);

  // Branch Selection
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [userBranch, setUserBranch] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [permsLoading, setPermsLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setPermsLoading(true);
      const response = await axios.get("/api/network_info");
      const { config: appConfig } = response.data;
      const currentBaseUrl = appConfig.mode === "client" ? `http://${appConfig.server_ip}:6034` : "";
      setBaseUrl(currentBaseUrl);

      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        const isAdminRole = checkIsAdmin(userObj);
        const isGroupAdminRole = checkIsGlobalAdmin(userObj);
        const branch = userObj.branch_name || userObj.branch || "";

        setIsCompanyAdmin(isAdminRole);
        setIsGroupAdmin(isGroupAdminRole);
        setUserBranch(branch);

        if (isAdminRole) {
          setSelectedCompanies(userObj.company ? [userObj.company] : []);
        }
        
        if (!isAdminRole && branch) {
          setSelectedBranches([branch]);
          setFormData(prev => ({ ...prev, branch_name: branch }));
        }

        if (isAdminRole || isGroupAdminRole) {
          const comps = userObj.companies || (userObj.company ? [userObj.company] : []);
          if (comps.length > 0) fetchBranches(comps[0]);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPermsLoading(false);
    }
  };

  const fetchBranches = async (companyName) => {
    try {
      if (!companyName) return;
      const response = await axios.get(`${baseUrl}/api/branches`, {
        headers: { 'X-Company-Name': companyName }
      });
      setAvailableBranches(response.data);
    } catch (err) {}
  };

  useEffect(() => {
    if (baseUrl !== "") fetchAllSettings();
  }, [baseUrl, selectedCompanies]);

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const headers = { 'X-Company-Name': (selectedCompanies.length > 0 ? selectedCompanies.join(',') : userObj.company) };
      if (!isCompanyAdmin && userBranch) headers['X-Branch-Name'] = userBranch;

      const response = await axios.get(`${baseUrl}/api/print_settings`, { headers });
      setSettingsList(response.data);
      const activeSetting = response.data.find(setting => setting.active);
      if (activeSetting) setSelectedId(activeSetting._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSetting = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/print_settings/${id}`);
      let data = response.data;
      
      // Ensure existing blocks have labels, or create initial blocks if missing
      if (!data.headerBlocks || data.headerBlocks.length === 0) {
        data.headerBlocks = [
          { id: Date.now().toString() + '1', text: '', align: 'center', label: 'Restaurant Name' },
          { id: Date.now().toString() + '2s', text: '', align: 'center', label: 'Street' },
          { id: Date.now().toString() + '2c', text: '', align: 'center', label: 'City' },
          { id: Date.now().toString() + '2p', text: '', align: 'center', label: 'Pincode' },
          { id: Date.now().toString() + '3', text: '', align: 'center', label: 'Phone' },
          { id: Date.now().toString() + '4', text: '', align: 'center', label: 'GSTIN' },
        ];
      } else {
        data.headerBlocks.forEach(b => { 
            if (!b.label) b.label = "Custom Line";
            // Scrub old dummy text from previous saves
            const dummies = ['Restaurant Name', 'Street', 'City', 'Pincode', 'Street, City, Pincode', 'Phone Number', 'Phone', 'GSTIN'];
            if (dummies.includes(b.text) || b.text.startsWith('Phone:') || b.text.startsWith('GSTIN:')) {
                b.text = '';
            }
        });
      }
      
      if (!data.footerBlocks || data.footerBlocks.length === 0) {
        data.footerBlocks = [
          { id: Date.now().toString() + '5', text: '', align: 'center', label: 'Thank You Message' },
          { id: Date.now().toString() + '6', text: '', align: 'center', label: 'Powered By' },
        ];
      } else {
        data.footerBlocks.forEach(b => { 
            if (!b.label) b.label = "Custom Line";
            if (b.text === 'Thank You' || b.text === 'Powered by manoj' || b.text === 'Thank You Message' || b.text === 'Powered By') {
                b.text = '';
            }
        });
      }

      setFormData(data);
      setSelectedBranches([data.branch_name]);
      setEditId(id);
      setShowList(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!editId && (!formData.headerBlocks || formData.headerBlocks.length === 0)) {
        setFormData(prev => ({
            ...prev,
            headerBlocks: [
              { id: 'h1', text: '', align: 'center', label: 'Restaurant Name' },
              { id: 'h2_street', text: '', align: 'center', label: 'Street' },
              { id: 'h2_city', text: '', align: 'center', label: 'City' },
              { id: 'h2_pincode', text: '', align: 'center', label: 'Pincode' },
              { id: 'h3', text: '', align: 'center', label: 'Phone Number' },
              { id: 'h4', text: '', align: 'center', label: 'GSTIN' },
            ],
            footerBlocks: [
              { id: 'f1', text: '', align: 'center', label: 'Thank You Message' },
              { id: 'f2', text: '', align: 'center', label: 'Powered By' },
            ]
        }));
    }
  }, [editId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      
      const activeComp = selectedCompanies[0] || userObj.company;
      const activeBranch = selectedBranches[0] || userBranch || "";
      
      if (editId) {
        await axios.put(`${baseUrl}/api/print_settings/${editId}`, { ...formData, branch_name: activeBranch }, { 
          headers: { 'X-Company-Name': activeComp, 'X-Branch-Name': activeBranch } 
        });
      } else {
        await axios.post(`${baseUrl}/api/print_settings`, { ...formData, branch_name: activeBranch }, { 
          headers: { 'X-Company-Name': activeComp, 'X-Branch-Name': activeBranch } 
        });
      }

      setWarningType('success');
      setWarningMessage('Print settings saved successfully!');
      await fetchAllSettings();
      setShowList(true);
      resetForm();
    } catch (err) {
      setWarningType('error');
      setWarningMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setWarningType('warning');
    setWarningMessage("Are you sure you want to delete this setting? This action cannot be undone.");
  };

  const executeDelete = async () => {
    if (!deleteId) return;
    setWarningMessage("");
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      await axios.delete(`${baseUrl}/api/print_settings/${deleteId}`, {
        headers: { 'X-Company-Name': (selectedCompanies.length > 0 ? selectedCompanies.join(',') : userObj.company) }
      });
      setWarningType('success');
      setWarningMessage('Deleted successfully!');
      fetchAllSettings();
    } catch (err) {
      setWarningType('error');
      setWarningMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  const handleUse = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const headers = { 'X-Company-Name': (selectedCompanies.length > 0 ? selectedCompanies.join(',') : userObj.company) };

      if (selectedId) {
        const setting = settingsList.find(s => s._id === selectedId);
        if (setting && setting.branch_name) headers['X-Branch-Name'] = setting.branch_name;
        await axios.put(`${baseUrl}/api/print_settings/set_active/${selectedId}`, {}, { headers });
      } else {
        await axios.put(`${baseUrl}/api/print_settings/deactivate_all`, {}, { headers });
      }
      setWarningType('success');
      setWarningMessage('Active setting updated!');
      fetchAllSettings();
    } catch (err) {
      setWarningType('error');
      setWarningMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      branch_name: "", headerBlocks: [], footerBlocks: []
    });
    setEditId(null);
  };

  if (permsLoading) return <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>;

  // Drag and Drop Logic for Blocks
  const onDragStart = (e, index, type) => {
    e.dataTransfer.setData('blockIndex', index);
    e.dataTransfer.setData('blockType', type);
  };

  const onDrop = (e, index, type) => {
    const dragIndex = e.dataTransfer.getData('blockIndex');
    const dragType = e.dataTransfer.getData('blockType');
    
    if (dragType !== type || dragIndex === index.toString()) return;

    const listName = type === 'header' ? 'headerBlocks' : 'footerBlocks';
    const newList = [...formData[listName]];
    const [draggedItem] = newList.splice(dragIndex, 1);
    newList.splice(index, 0, draggedItem);
    
    setFormData(prev => ({ ...prev, [listName]: newList }));
  };

  const addBlock = (type) => {
    const listName = type === 'header' ? 'headerBlocks' : 'footerBlocks';
    setFormData(prev => ({
      ...prev,
      [listName]: [...(prev[listName] || []), { id: Date.now().toString(), text: '', align: 'center', label: 'Custom Line' }]
    }));
  };

  const updateBlock = (type, index, field, value) => {
    const listName = type === 'header' ? 'headerBlocks' : 'footerBlocks';
    const newList = [...formData[listName]];
    newList[index][field] = value;
    setFormData(prev => ({ ...prev, [listName]: newList }));
  };

  const removeBlock = (type, index) => {
    const listName = type === 'header' ? 'headerBlocks' : 'footerBlocks';
    const newList = [...formData[listName]];
    newList.splice(index, 1);
    setFormData(prev => ({ ...prev, [listName]: newList }));
  };

  const renderBlockEditor = (type) => {
    const blocks = type === 'header' ? formData.headerBlocks : formData.footerBlocks;
    return (
      <div style={{ marginTop: '20px', background: '#f8fafc', borderRadius: '15px', padding: '20px', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h4 style={{ margin: 0, color: '#334155', fontSize: '15px' }}>{type === 'header' ? 'Receipt Header (Top)' : 'Receipt Footer (Bottom)'}</h4>
          <button type="button" onClick={() => addBlock(type)} style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <FaPlus /> Add Line
          </button>
        </div>
        
        {blocks && blocks.map((block, index) => (
          <div 
            key={block.id}
            draggable
            onDragStart={(e) => onDragStart(e, index, type)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, index, type)}
            style={{ background: '#fff', padding: '15px', borderRadius: '10px', marginBottom: '15px', boxShadow: '0 2px 5px rgba(0,0,0,0.02)', border: '1px solid #e2e8f0', cursor: 'grab' }}
          >
            {/* Block Label shown on top */}
            <div style={{ marginBottom: '8px' }}>
               <label style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>{block.label}</label>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaGripLines style={{ color: '#cbd5e1', fontSize: '16px', flexShrink: 0 }} />
              
              <input 
                type="text" 
                value={block.text} 
                onChange={(e) => updateBlock(type, index, 'text', e.target.value)} 
                placeholder={`Enter ${block.label.toLowerCase()}...`}
                style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none', backgroundColor: '#f8fafc' }}
              />
              
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '6px', overflow: 'hidden' }}>
                <button type="button" onClick={() => updateBlock(type, index, 'align', 'left')} style={{ padding: '8px 12px', background: block.align === 'left' ? '#e2e8f0' : 'transparent', border: 'none', color: '#475569', cursor: 'pointer' }}><FaAlignLeft /></button>
                <button type="button" onClick={() => updateBlock(type, index, 'align', 'center')} style={{ padding: '8px 12px', background: block.align === 'center' ? '#e2e8f0' : 'transparent', border: 'none', color: '#475569', cursor: 'pointer' }}><FaAlignCenter /></button>
                <button type="button" onClick={() => updateBlock(type, index, 'align', 'right')} style={{ padding: '8px 12px', background: block.align === 'right' ? '#e2e8f0' : 'transparent', border: 'none', color: '#475569', cursor: 'pointer' }}><FaAlignRight /></button>
              </div>
              
              <button type="button" onClick={() => removeBlock(type, index)} style={{ padding: '8px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f8', padding: '30px', fontFamily: "'Nunito', sans-serif" }}>
      <style>
        {`
          .premium-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
          }
          .premium-table th {
            background-color: #F8F9FD;
            color: #777777;
            font-weight: 600;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 16px 24px;
            text-align: left;
            border-bottom: 2px solid #E2E6EF;
            white-space: nowrap;
          }
          .premium-table td {
            padding: 20px 24px;
            border-bottom: 1px solid #F1F3F8;
            color: #4A4A5A;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s;
          }
          .premium-table tbody tr {
            transition: all 0.2s ease;
          }
          .premium-table tbody tr:hover td {
            background-color: #F8F9FD;
          }
          .action-btn {
            width: 48px;
            height: 48px;
            border-radius: 14px;
            display: flex;
            justify-content: center;
            align-items: center;
            border: none;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            opacity: 1;
          }
          .premium-table tbody tr:hover .action-btn {
            opacity: 1;
          }
          .action-btn:hover {
            transform: translateY(-2px) !important;
            opacity: 1;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          .action-btn-edit { background: #FFF4E5; color: #FF9800; }
          .action-btn-edit:hover { background: #FF9800; color: #ffffff; box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3); }
          .action-btn-delete { background: #FFEBEE; color: #F44336; }
          .action-btn-delete:hover { background: #F44336; color: #ffffff; box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3); }
        `}
      </style>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', background: '#fff', padding: '20px 30px', borderRadius: '15px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button onClick={() => showList ? window.history.back() : setShowList(true)} style={{ background: '#f1f5f9', border: 'none', width: '45px', height: '45px', borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
            <FaArrowLeft style={{ color: '#475569', fontSize: '18px' }} />
          </button>
          <h2 style={{ margin: 0, color: '#1e293b', fontSize: '24px', fontWeight: '800' }}>PRINT SETTINGS</h2>
        </div>
        <button onClick={() => setShowCustomizeModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}>
          <FaCogs /> Customize
        </button>
      </div>

      {showList ? (
        <div style={{ background: '#fff', borderRadius: '20px', padding: '30px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '20px' }}>Saved Print Settings</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { resetForm(); setShowList(false); }} style={{ padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}><FaPlus /> Create New</button>
              <button onClick={handleUse} style={{ padding: '10px 20px', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}>Activate Selected</button>
            </div>
          </div>
          <table className="premium-table">
            <thead>
              <tr>
                <th>Active</th>
                <th>Restaurant Name</th>
                <th>Branch</th>
                <th style={{ backgroundColor: '#10b981', color: '#ffffff', textAlign: 'center', borderTopRightRadius: '12px' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {settingsList.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#777777', fontSize: '15px', fontWeight: '600' }}>
                    No saved print settings found.
                  </td>
                </tr>
              ) : (
                settingsList.map(s => (
                  <tr key={s._id}>
                    <td><input type="radio" checked={selectedId === s._id} onChange={() => setSelectedId(s._id)} style={{ transform: 'scale(1.5)', accentColor: '#604BE8', cursor: 'pointer' }} /></td>
                    <td style={{ fontWeight: '700', color: '#1B1B29' }}>
                      {s.restaurantName || (s.headerBlocks && s.headerBlocks[0] && s.headerBlocks[0].text) || 'Unnamed Layout'}
                    </td>
                    <td>
                      {s.branch_name ? (
                         <span style={{ padding: '4px 12px', borderRadius: '6px', background: '#eff2f9', color: '#604BE8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase' }}>
                           {s.branch_name}
                         </span>
                      ) : 'Global'}
                    </td>
                    <td style={{ padding: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        <button type="button" onClick={() => fetchSetting(s._id)} className="action-btn action-btn-edit" title="Edit Print Setting">
                          <FaEdit style={{ fontSize: '18px' }} />
                        </button>
                        <button type="button" onClick={() => handleDelete(s._id)} className="action-btn action-btn-delete" title="Delete Print Setting">
                          <FaTrash style={{ fontSize: '18px' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '30px' }}>
          {/* Form Content */}
          <div style={{ background: '#fff', borderRadius: '20px', padding: '30px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <form onSubmit={handleSubmit}>
              
              {/* Branch Selection */}
              {(isCompanyAdmin || isGroupAdmin) && availableBranches.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>
                    📍 SELECT BRANCHES (OPTIONAL):
                  </label>
                  <div style={{ padding: '15px', background: '#f8fafc', borderRadius: '8px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                    {availableBranches.map(br => (
                      <label key={br.branch_name || br} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#0f172a', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedBranches.includes(br.branch_name || br)}
                          onChange={() => {
                            const val = br.branch_name || br;
                            setSelectedBranches(prev => prev.includes(val) ? prev.filter(b => b !== val) : [val]);
                          }}
                        />
                        {br.branch_name || br}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Editable Blocks Layout Builder */}
              <h3 style={{ margin: '0 0 10px', color: '#1e293b', fontSize: '20px' }}>Layout Builder</h3>
              <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14px' }}>Add lines, type your text, drag to reorder, and click alignment buttons to position them.</p>

              {renderBlockEditor('header')}
              {renderBlockEditor('footer')}

              <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center', gap: '15px' }}>
                <button type="submit" disabled={loading} style={{ padding: '12px 30px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '25px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'Saving...' : 'Save Layout'}
                </button>
                <button type="button" onClick={() => { fetchAllSettings(); setShowList(true); }} style={{ padding: '12px 30px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '25px', fontSize: '16px', fontWeight: '700', cursor: 'pointer' }}>
                  View Saved
                </button>
              </div>
            </form>
          </div>

          {/* Interactive Live Receipt Preview */}
          <div style={{ background: '#fff', borderRadius: '20px', padding: '0', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', minHeight: '600px', display: 'flex', flexDirection: 'column', position: 'sticky', top: '30px' }}>
            <div style={{ background: '#f8fafc', padding: '15px', borderBottom: '1px solid #e2e8f0', borderRadius: '20px 20px 0 0', textAlign: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Live Receipt Preview</h3>
              <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#64748b' }}>Updates automatically as you build</p>
            </div>
            
            <div style={{ padding: '25px', fontFamily: '"Courier New", Courier, monospace', fontSize: '14px', color: '#000', lineHeight: '1.4', flex: 1, display: 'flex', flexDirection: 'column' }}>
              
              {/* HEADER PREVIEW */}
              <div style={{ marginBottom: '20px', width: '100%' }}>
                {formData.headerBlocks && formData.headerBlocks.map(block => (
                  <div key={`prev-${block.id}`} style={{ textAlign: block.align, whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: '20px' }}>
                    {block.text || <span style={{ color: '#94a3b8' }}>{block.label}</span>}
                  </div>
                ))}
              </div>

              {/* Empty Flexible Space (The Gap) */}
              <div style={{ flex: 1, minHeight: '150px', borderLeft: '2px dashed #e2e8f0', borderRight: '2px dashed #e2e8f0', margin: '10px 0', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#94a3b8', fontSize: '12px', textAlign: 'center' }}>
                  (Dynamic Order Details printed here)
                </div>
              </div>

              {/* FOOTER PREVIEW */}
              <div style={{ marginTop: '20px', width: '100%' }}>
                {formData.footerBlocks && formData.footerBlocks.map(block => (
                  <div key={`prev-${block.id}`} style={{ textAlign: block.align, whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: '20px' }}>
                    {block.text || <span style={{ color: '#94a3b8' }}>{block.label}</span>}
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      )}

      {warningMessage && (
        <div className="premium-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div className="premium-modal-box" style={{
            background: '#ffffff',
            borderRadius: '24px',
            padding: '40px 32px',
            width: '90%',
            maxWidth: '440px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '200px',
              height: '200px',
              background: warningType === 'success' ? 'radial-gradient(circle, rgba(6, 214, 160, 0.15) 0%, rgba(255,255,255,0) 70%)' : warningType === 'warning' ? 'radial-gradient(circle, rgba(255, 159, 4, 0.15) 0%, rgba(255,255,255,0) 70%)' : 'radial-gradient(circle, rgba(255, 89, 94, 0.15) 0%, rgba(255,255,255,0) 70%)',
              zIndex: 0,
              pointerEvents: 'none'
            }}></div>

            <div className="premium-modal-icon" style={{
              width: '84px',
              height: '84px',
              borderRadius: '50%',
              background: warningType === 'success' ? '#ecfdf5' : warningType === 'warning' ? '#fffbeb' : '#ffebee',
              color: warningType === 'success' ? '#06D6A0' : warningType === 'warning' ? '#FF9F04' : '#FF595E',
              border: `2px solid ${warningType === 'success' ? '#a7f3d0' : warningType === 'warning' ? '#fde68a' : '#fca5a5'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '38px',
              marginBottom: '24px',
              boxShadow: warningType === 'success' ? '0 10px 25px -5px rgba(6, 214, 160, 0.2)' : warningType === 'warning' ? '0 10px 25px -5px rgba(255, 159, 4, 0.2)' : '0 10px 25px -5px rgba(255, 89, 94, 0.2)',
              zIndex: 1
            }}>
              {warningType === 'success' ? <FaUserCheck /> : <FaExclamationTriangle />}
            </div>

            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1e293b',
              margin: '0 0 12px 0',
              zIndex: 1,
              fontFamily: `'Gilroy', 'Inter', sans-serif`
            }}>
              {warningType === 'success' ? "Success" : warningType === 'warning' ? "Notice" : "Error"}
            </h3>

            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.6',
              margin: '0 0 32px 0',
              zIndex: 1,
              fontWeight: '500'
            }}>
              {warningMessage}
            </p>

            <div className="premium-modal-actions" style={{
              display: 'flex',
              gap: '16px',
              width: '100%',
              zIndex: 1
            }}>
              <button 
                type="button"
                onClick={() => {
                  setWarningMessage("");
                  setDeleteId(null);
                }}
                style={{
                  flex: 1,
                  height: '48px',
                  background: '#f1f5f9',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={() => {
                  if (warningType === 'warning' && deleteId) {
                    executeDelete();
                  } else {
                    setWarningMessage("");
                  }
                }} 
                style={{
                  flex: 1,
                  height: '48px',
                  background: warningType === 'success' ? '#06D6A0' : warningType === 'warning' ? '#FF9F04' : '#FF595E',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '14px',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  boxShadow: warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : warningType === 'warning' ? '0 10px 20px -5px rgba(255, 159, 4, 0.3)' : '0 10px 20px -5px rgba(255, 89, 94, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                {warningType === 'warning' && deleteId ? 'Delete' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOMIZATION MODAL */}
      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        targetDocType="Print Settings"
      />
    </div>
  );
};

export default PrintSettingsPage;
